<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import BenvoxLogo from "./BenvoxLogo.vue";

/**
 * O INSTANTE ENTRE ABRIR E ESTAR DENTRO — a marca, e nenhuma palavra.
 *
 * ── O QUE ISTO SUBSTITUI ──────────────────────────────────────────────────
 *
 * Uma linha de texto cinzento — "Restaurando sessão…" — no meio de uma tela
 * branca, a cada carregamento. Ela contava ao utilizador um detalhe de
 * implementação (que existe uma sessão, e que ela está a ser restaurada) num
 * momento em que a única pergunta dele é "já abriu?".
 *
 * ── POR QUE NENHUMA PALAVRA ───────────────────────────────────────────────
 *
 * Porque uma palavra aqui tem de existir em todos os idiomas, e este é o único
 * ecrã do produto que desenha ANTES de qualquer escolha de língua ter sido lida
 * de volta. Uma frase que falta desenha a própria chave — `session.restoring` —
 * no meio do ecrã, que é exatamente o pior lugar para isso acontecer. A marca
 * não tem idioma.
 *
 * ── POR QUE ELE DEMORA A APARECER ─────────────────────────────────────────
 *
 * O SEGREDO desta tela é que quase nunca se vê. Restaurar a sessão é uma ida ao
 * servidor: numa rede boa são 80 ms, e um ecrã de carregamento que pisca por
 * 80 ms é pior do que ecrã nenhum — o olho regista o salto, não a informação.
 *
 * Então ela espera. Abaixo de `DELAY_MS` não desenha nada e a aplicação
 * simplesmente aparece; acima, desenha — porque aí a espera é real e um ecrã
 * vazio passa a ler-se como avaria. É a mesma razão pela qual um botão só mostra
 * um `spinner` depois de um momento.
 */
const DELAY_MS = 300;

const shown = ref(false);
let timer: ReturnType<typeof setTimeout> | undefined;

onMounted(() => {
	timer = setTimeout(() => {
		shown.value = true;
	}, DELAY_MS);
});

// Limpo ao desmontar: a sessão que volta em 90 ms tira este componente do ar
// antes do prazo, e um `setTimeout` órfão a escrever num `ref` morto é o tipo de
// aviso que aparece no console de outra pessoa, meses depois.
onUnmounted(() => clearTimeout(timer));
</script>

<template>
	<!--
		`aria-busy` e não uma frase: quem lê a tela ouve "a carregar" na língua do
		próprio leitor, que é a única maneira de dizer isto sem escolher um idioma.
	-->
	<div
		class="flex min-h-dvh items-center justify-center bg-bg"
		role="status"
		aria-busy="true"
	>
		<BenvoxLogo
			v-if="shown"
			class="h-6 w-auto animate-pulse text-fg-subtle motion-reduce:animate-none"
		/>
	</div>
</template>
